import { defineStore } from 'pinia'

import {
    getVendors,
    updateVendor,
    updateVendorsStatus,
    deleteVendor,
    createVendor,
    getAllVendors,
    getVendorFiltersAndSorting,
    countersVendor,
    resetCapsVendor,
} from '@/api/vendor-routes'

import { getFilterOnPageInLocalStorageByWorkspaceId, setFilterOnPageInLocalStorageByWorkspaceId } from '@/helpers/save-sorting-local-storage-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useSortFilterTableStore } from '@/store/sortFilterTableStore'
import { useDetailVendorStore } from '@/store/call-tracking/detailVendorStore'

import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'

export const useVendorsStore = defineStore('vendors', {
    state: () => ({
        loading: false,
        actionLoading: false,
        statusLoading: false,
        allLoading: false,
        liveInterval: null,
        dataCounters: {},
        warningStatuses: {},
        countersLoading: {},
        dontShowCounterArrow: true,
        allVendors: [],
        items: [],
        slugsForCounters: [],
        selected: [],
        page: 1,
        total: 1,
        onPage: 25,
        search: null,
    }),

    actions: {
        async goToLoadVendorFiltersAndSorting() {
            const sortFilterTableStore = useSortFilterTableStore()
            const { filters, sorting } = sortFilterTableStore.getTable(SETTINGS_HEADER_TABLE.vendor)

            if (filters.length) {
                sortFilterTableStore.setFilters(SETTINGS_HEADER_TABLE.vendor, filters)
                sortFilterTableStore.setSorting(SETTINGS_HEADER_TABLE.vendor, sorting)
                return
            }

            const { success, payload, message } = await getVendorFiltersAndSorting()

            if (success) {
                sortFilterTableStore.setFilters(SETTINGS_HEADER_TABLE.vendor, payload.filters)
                sortFilterTableStore.setSorting(SETTINGS_HEADER_TABLE.vendor, payload.sorting)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToLoadVendors() {
            const permissionsStore = usePermissionsStore()

            if (!permissionsStore.permissions.vendors.view) {
                return
            }

            const sortFilterTableStore = useSortFilterTableStore()
            const { filtersForSend, sortingForSend } = sortFilterTableStore.getTable(SETTINGS_HEADER_TABLE.vendor)

            this.clearSelected()

            this.loading = true

            const { success, payload, message } = await getVendors({
                page: this.page,
                search: this.search,
                onPage: this.onPage,
                ...filtersForSend,
                ...sortingForSend,
            })

            if (success) {
                const { data = [], current_page, last_page } = payload
                this.items = data
                this.page = current_page
                this.total = last_page

                this.checkValidPage()
                this.setLoadCountersInterval(this.items.map((vendor) => vendor.slug))
            } else {
                this.loading = false
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        setLoadCountersInterval(slugsForCounters = []) {
            this.removeCountersInterval()

            if (!slugsForCounters.length) {
                return
            }

            this.slugsForCounters = slugsForCounters

            this.goToLoadCountersVendor()

            this.liveInterval = setInterval(() => {
                this.goToLoadCountersVendor()
            }, 30000)
        },

        async goToLoadCountersVendor() {
            const defaultStore = useDefaultStore()

            const formData = {
                vendors: this.slugsForCounters,
            }

            if (formData.vendors.length) {
                const { success, payload, message } = await countersVendor(formData)

                if (success) {
                    this.parseDataCounters(payload)
                } else {
                    defaultStore.setErrorMessage({ message })
                }
            }
        },

        removeCountersInterval() {
            clearInterval(this.liveInterval)
        },

        parseDataCounters(payload = []) {
            if (!payload.length) {
                return
            }

            payload.forEach((item) => {
                const {
                    slug, capsCounter = {}, warningStatus,
                } = item
                this.dataCounters[slug] = capsCounter
                this.warningStatuses[slug] = warningStatus
            })

            setTimeout(() => {
                this.dontShowCounterArrow = false
            }, 1000)
        },

        async goToUpdateCountersVendor({ formData, counterName }) {
            const detailVendorStore = useDetailVendorStore()

            const counterLoadingKey = `${formData.slug}-${counterName}`

            this.changeCountersLoading({ counterLoadingKey })

            detailVendorStore.setVendorSlug(formData.slug)
            detailVendorStore.parsePayload(formData)

            const { success } = await detailVendorStore.goToUpdateVendor({})

            if (success) {
                this.changeVendorAfterUpdate(formData)
            }

            this.changeCountersLoading({ counterLoadingKey, value: false })
        },

        changeCountersLoading({ counterLoadingKey, value = true }) {
            this.countersLoading[counterLoadingKey] = value
        },

        changeVendorAfterUpdate(item) {
            const index = this.items.findIndex((i) => i.slug === item.slug)

            if (index + 1) {
                this.items.splice(index, 1, item)
            }
        },

        async goToLoadVendorsAll() {
            const permissionsStore = usePermissionsStore()

            if (!permissionsStore.permissions.vendors.view) {
                return
            }

            const defaultStore = useDefaultStore()

            this.allLoading = true

            const { success, payload, message } = await getAllVendors()

            if (success) {
                this.allVendors = payload
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.allLoading = false
        },

        async goToCreateVendor(formData) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, payload, message } = await createVendor(formData)

            if (success) {
                this.goToLoadVendors()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success, payload }
        },

        async goToUpdateVendor({ formData, slug }) {
            const defaultStore = useDefaultStore()

            if (this.actionLoading) {
                return
            }

            this.actionLoading = true

            const { success, message } = await updateVendor({
                formData,
                slug,
            })

            if (success) {
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false
        },

        async goToUpdateVendorsStatus({ slugs, status_id }) {
            const defaultStore = useDefaultStore()

            if (this.statusLoading) {
                return
            }

            this.statusLoading = true

            const { success, payload, message } = await updateVendorsStatus({ slugs, status_id })

            if (success) {
                this.updateAfterChangeStatus(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.statusLoading = false

            return success
        },

        async goToDeleteVendor(slugs) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await deleteVendor({ slugs })

            if (success) {
                this.goToLoadVendors()
                this.checkValidPage()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return success
        },

        async goToResetVendorCaps(item) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await resetCapsVendor(item.slug)

            if (success) {
                this.goToLoadCountersVendor()

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return success
        },

        updateAfterChangeStatus(vendors) {
            vendors.forEach((vendor) => {
                const index = this.items.findIndex((i) => i.slug === vendor.slug)

                if (index + 1) {
                    this.items[index].status_id = vendor.status_id
                }
            })
        },

        checkValidPage() {
            if (this.page > this.total) {
                this.changePage(this.total)
            } else {
                this.loading = false
            }
        },

        onSearch(searchValue) {
            this.search = searchValue
            this.changePage(1)
        },

        changePage(newPage) {
            this.page = newPage

            this.goToLoadVendors()
        },

        changeSelectedItems(newSelectedItems) {
            this.selected = newSelectedItems
        },

        clearSelected() {
            this.selected = []
        },

        clearItems() {
            this.items = []
        },

        changeOnPage(onPage) {
            this.onPage = onPage

            setFilterOnPageInLocalStorageByWorkspaceId(onPage, SETTINGS_HEADER_TABLE.vendor)

            this.goToLoadVendors()
        },

        getLocalStorageOnPage() {
            const savedOnPage = getFilterOnPageInLocalStorageByWorkspaceId(SETTINGS_HEADER_TABLE.vendor)

            this.onPage = savedOnPage ? Number(savedOnPage) : 25
        },
    },
})
